import React from "react";
import { UseFormGetValues } from "react-hook-form";
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FaFax, FaPhone } from "react-icons/fa";
import Grid from "@mui/material/Grid";
import { get, isEmpty } from "lodash";

import colors from "../../../../styles/colors/colors";
import fontWeight from "../../../../styles/mui/fontWeight";
import {
  ReviewDetailEmergencyContactTable,
  ReviewDetailsGridHeading,
  ReviewDetailsRowGrid,
  ReviewDetailsLeftColumnGrid,
  ReviewDetailsRightColumnGrid,
  ReviewDetailsLastRowGrid,
  ReviewDetailsFields,
  ReviewDetailsEMCtable,
  ReviewDetailsFeildsGrid,
  ReviewDetailsOutterGrid,
  TableHeadStyle,
  DocumentsListGrid,
  ReviewDocumentTypeIcon,
  DocDividerStyle,
  DocumentAlignment,
  DividerMargin,
} from "../../styles/Review";
import { getFormattedName, extractDocType } from "../../../../utils";
import { getDocTypeIcon } from "../../../../utils/DocIcons";
import { formatDate } from "../../../../utils/DateTime";
import { DateFormats } from "../../../../constants/Date";
import { DocumentType } from "../../../../constants/DocumentUpload";

export interface PropsFromState {
  getValues: UseFormGetValues<any>;
}
export interface PropsFromDispatch {
  getLanguageMasterResp: () => void;
  getRelationshipMasterResp: () => void;
}

type AllProps = PropsFromState;
interface Props {
  color: string;
  extention?: string | null;
  heading: string;
  value: string;
  Icon?: React.ElementType;
  isIcon?: any;
  gridSize: number;
}

const ReviewFields: React.FC<Props> = ({
  heading,
  color,
  value,
  gridSize,
}: Props) => {
  return (
    <Grid size={{ xs: gridSize }} sx={ReviewDetailsFeildsGrid}>
      <Typography
        variant="body1"
        fontWeight={fontWeight.Weight[3]}
        color={colors.fonts[2]}
      >
        {heading}
      </Typography>
      <Typography
        variant={"subtitle2"}
        fontWeight={fontWeight.Weight[5]}
        color={color}
        pt={"0.5rem"}
        pr={"1.5rem"}
        lineHeight={1.3}
      >
        {value}
      </Typography>
    </Grid>
  );
};

const tableHeadTitle = (title: string) => {
  return (
    <TableCell>
      <Typography
        variant="body1"
        fontWeight={fontWeight.Weight[3]}
        color={colors.fonts[2]}
      >
        {title}
      </Typography>
    </TableCell>
  );
};

const ReviewDetails: React.FC<any> = ({ getValues }: AllProps) => {
  const emergencyContact = getValues("emergencyContact");
  const serviceRequest = getValues("serviceRequest");
  const diagnosisCode = getValues("diagnosisCode");
  const referralBlobDetail = getValues("referralBlobDetail");

  const referralSourcePhone = () => {
    const phoneNo = getValues("referralSource.referralSourcePhoneNo");
    const phoneExt = getValues("referralSource.referralSourceExtension");
    if (phoneNo && phoneExt) {
      return `${phoneNo} Ext: ${phoneExt}`;
    } else if (phoneNo) {
      return phoneNo;
    }
    return "";
  };

  return (
    <Grid container sx={ReviewDetailsOutterGrid}>
      <Grid container size={{ xs: 12 }} sx={ReviewDetailsRowGrid}>
        <Grid size={{ xs: 4.8 }} sx={ReviewDetailsLeftColumnGrid}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              MEMBER DETAILS
            </Typography>
          </Box>
          <Grid size={{ xs: 12 }} sx={ReviewDetailsFields}>
            <ReviewFields
              heading="Last Name :"
              color={colors.fonts[25]}
              value={getFormattedName(getValues("patientDetail.lastName"))}
              gridSize={4.5}
            />
            <ReviewFields
              heading="First Name :"
              color={colors.fonts[25]}
              value={
                getFormattedName(getValues("patientDetail.firstName")) || ""
              }
              gridSize={4.5}
            />
            <ReviewFields
              heading="Date of Birth :"
              color={colors.fonts[25]}
              value={
                getValues("patientDetail.dateOfBirth") || ""
                  ? formatDate(
                      getValues("patientDetail.dateOfBirth"),
                      DateFormats.MM_DD_YYYY
                    )
                  : ""
              }
              gridSize={3}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 7.2 }} sx={ReviewDetailsRightColumnGrid}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              ADDRESS DETAILS
            </Typography>
          </Box>
          <Grid container sx={ReviewDetailsFields}>
            <ReviewFields
              heading="Street Name :"
              color={colors.fonts[25]}
              value={`${
                getValues("patientDetail.residentialAddress.streetName1") || ""
              } ${
                getValues("patientDetail.residentialAddress.streetName2") || ""
              }`.trim()}
              gridSize={3.5}
            />
            <ReviewFields
              heading="County :"
              color={colors.fonts[25]}
              value={getValues("patientDetail.residentialAddress.county")}
              gridSize={2.4}
            />
            <ReviewFields
              heading="City :"
              color={colors.fonts[25]}
              value={getValues("patientDetail.residentialAddress.city")}
              gridSize={2.3}
            />
            <ReviewFields
              heading="State :"
              color={colors.fonts[25]}
              value={getValues("patientDetail.residentialAddress.patientState")}
              gridSize={2.3}
            />
            <ReviewFields
              heading="Postal code :"
              color={colors.fonts[25]}
              value={getValues("patientDetail.residentialAddress.zipCode")}
              gridSize={1.5}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={0} sx={ReviewDetailsRowGrid}>
        <Grid size={{ xs: 3.3 }} sx={ReviewDetailsLeftColumnGrid}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              PLAN DETAILS
            </Typography>
          </Box>
          <Grid size={{ xs: 12 }} sx={ReviewDetailsFields}>
            <ReviewFields
              heading="Subscriber ID : "
              color={colors.fonts[25]}
              value={getValues("patientDetail.subscriberId") || ""}
              gridSize={6.5}
            />
            <ReviewFields
              heading="Health Plan :"
              color={colors.fonts[25]}
              value={getValues("patientDetail.eligibilityPayerName") || ""}
              gridSize={5.5}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 8.7 }} sx={ReviewDetailsRightColumnGrid}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              EMERGENCY CONTACT DETAILS
            </Typography>
          </Box>
          <Grid container sx={ReviewDetailsEMCtable}>
            <TableContainer component={Grid}>
              <Table
                sx={ReviewDetailEmergencyContactTable}
                aria-label="simple table"
              >
                <TableHead sx={TableHeadStyle}>
                  <TableRow>
                    {tableHeadTitle("Emergency Contact Name :")}
                    {tableHeadTitle(" Relationship :")}
                    {tableHeadTitle(" Phone :")}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[emergencyContact].map((data: any) => (
                    <TableRow key={get(data, "contactName", "")}>
                      <TableCell sx={{ width: "15%" }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={fontWeight.Weight[5]}
                          color={colors.fonts[25]}
                        >
                          {getFormattedName(get(data, "contactName", ""))}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: "11%" }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={fontWeight.Weight[5]}
                          color={colors.fonts[25]}
                        >
                          {get(data, "relationshipName", "")}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: "29%", minWidth: "6.5rem" }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={fontWeight.Weight[5]}
                          color={colors.fonts[25]}
                        >
                          {get(data, "phoneNo", "")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={0} sx={ReviewDetailsRowGrid}>
        <Grid size={{ xs: 12 }}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              REFERRAL DETAILS
            </Typography>
          </Box>
          <Grid size={{ xs: 5 }} sx={ReviewDetailsFields}>
            <ReviewFields
              heading="Type: "
              color={colors.fonts[25]}
              value={getValues("referralSource.facilityTypeName") || ""}
              gridSize={4}
            />
            <ReviewFields
              heading="Name:"
              color={colors.fonts[25]}
              value={getValues("referralSource.facilityName") || ""}
              gridSize={3}
            />
            <ReviewFields
              heading="Phone:"
              color={colors.fonts[25]}
              Icon={FaPhone}
              isIcon={true}
              value={referralSourcePhone()}
              gridSize={2.5}
            />
            <ReviewFields
              heading="Fax No:"
              color={colors.fonts[25]}
              Icon={FaFax}
              isIcon={true}
              value={getValues("referralSource.referralSourceFax") || ""}
              gridSize={3}
            />
          </Grid>
          <Grid size={{ xs: 10 }} sx={ReviewDetailsFields}>
            <ReviewFields
              heading="Physician Name:"
              color={colors.fonts[25]}
              value={getValues("orderingPhysician.physicianName") || ""}
              gridSize={1.84}
            />
            <ReviewFields
              heading="Street Name:"
              color={colors.fonts[25]}
              value={`${getValues("orderingPhysician.streetName1") || ""} ${
                getValues("orderingPhysician.streetName2") || ""
              }`.trim()}
              gridSize={2.78}
            />
            <ReviewFields
              heading="County:"
              color={colors.fonts[25]}
              value={getValues("orderingPhysician.county") || ""}
              gridSize={1.5}
            />
            <ReviewFields
              heading="City:"
              color={colors.fonts[25]}
              value={getValues("orderingPhysician.city") || ""}
              gridSize={1.5}
            />
            <ReviewFields
              heading="State:"
              color={colors.fonts[25]}
              value={getValues("orderingPhysician.physicianState") || ""}
              gridSize={1.5}
            />
            <ReviewFields
              heading="Postal code:"
              color={colors.fonts[25]}
              value={getValues("orderingPhysician.zipCode") || ""}
              gridSize={1.5}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={0} sx={ReviewDetailsRowGrid}>
        <Grid size={{ xs: 12 }}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              ICD 10
            </Typography>
          </Box>
          <Grid size={{ xs: 12 }} pt={"1.5rem"}>
            {Array.isArray(diagnosisCode) &&
              diagnosisCode.map((data: any) => (
                <Typography
                  key={data.diagnosisCode}
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[5]}
                  color={colors.fonts[4]}
                  pb={"1rem"}
                >
                  {data.diagnosisCode + " - " + data.diagnosisCodeDescription}
                </Typography>
              ))}
          </Grid>
        </Grid>
      </Grid>
      <Grid container size={{ xs: 12 }} spacing={0} sx={ReviewDetailsRowGrid}>
        <Grid size={{ xs: 12 }}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
            >
              SERVICE REQUESTS
            </Typography>
          </Box>
          <Grid size={{ xs: 12 }} display={"flex"} pt={"1rem"}>
            <Grid size={{ xs: 3.85 }}>
              <Grid>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[5]}
                  color={colors.fonts[25]}
                >
                  Care Type Required
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }} pt={"0.8rem"}>
                {Array.isArray(serviceRequest?.careType) &&
                  serviceRequest.careType.map((data: any) => (
                    <Typography
                      key={data.careTypeName}
                      variant="subtitle2"
                      fontWeight={fontWeight.Weight[5]}
                      color={colors.fonts[4]}
                      pb={"0.8rem"}
                    >
                      - {data.careTypeName}
                    </Typography>
                  ))}
              </Grid>
            </Grid>
            <Grid size={{ xs: 8.15 }}>
              <Grid>
                <Typography
                  variant="subtitle2"
                  fontWeight={fontWeight.Weight[5]}
                  color={colors.fonts[6]}
                >
                  Disciplines requested
                </Typography>
              </Grid>
              <Grid size={{ xs: 12 }} pt={"0.8rem"}>
                {Array.isArray(serviceRequest?.discipline) &&
                  serviceRequest.discipline.map((data: any) => (
                    <Typography
                      key={data.disciplineName}
                      variant="subtitle2"
                      fontWeight={fontWeight.Weight[5]}
                      color={colors.fonts[4]}
                      pb={"0.8rem"}
                    >
                      - {data.disciplineName}
                    </Typography>
                  ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        size={{ xs: 12 }}
        spacing={0}
        sx={ReviewDetailsLastRowGrid}
      >
        <Grid size={{ xs: 12 }}>
          <Box sx={ReviewDetailsGridHeading}>
            <Typography
              variant="subtitle1"
              color={colors.fonts[20]}
              fontWeight={fontWeight.Weight[5]}
              pb={"1rem"}
            >
              DOCUMENTS:
            </Typography>
          </Box>
          <Grid size={{ xs: 12 }} sx={DocumentsListGrid}>
            {Array.isArray(referralBlobDetail) &&
              referralBlobDetail.map((data: any, index: number) => (
                <Box key={data.originalDocumentName}>
                  {data.originalDocumentName && (
                      <Box sx={DocumentAlignment}>
                        <Box sx={ReviewDocumentTypeIcon}>
                          {getDocTypeIcon(!isEmpty(data.mediaType) ? extractDocType(data.mediaType) : DocumentType.INVALID)}
                        </Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={fontWeight.Weight[5]}
                          color={colors.fonts[4]}
                          mr={"1rem"}
                        >
                          {data.originalDocumentName}
                        </Typography>
                        <Box component={"span"} margin={DividerMargin}>
                          {referralBlobDetail.length - 1 !== index && (
                            <Divider
                              sx={DocDividerStyle}
                              orientation="vertical"
                              flexItem
                            />
                          )}
                        </Box>
                      </Box>
                  )}
                </Box>
              ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ReviewDetails;
